import type { AirRequestFileType } from '@/app/(main)/air-request/types';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { ResponseDataWithPagination, ResponseType } from './apiTypes';
import type { GetClientData, GetClientPreferencesData, LoyaltyNumbersData } from './clientsApiTypes';
import type { ClientCrmStatus, Nullable } from './common';

export interface AddCreditCardPresignedUrlData {
  url: string;
  secret: string;
}

export interface AddCreditCardToVaultData {
  reference: string;
  stored_at: string;
  token: string;
  user: string;
}

export interface BillingAddressData {
  country: string;
  state?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  zip: string;
}

export interface AddCreditCardData extends BillingAddressData {
  last4: string;
  token: string;
  reference: string;
  card_type: string;
  cvv: string;
  raw_phone_number?: string;
  nickname?: string;
  note?: string;
  card_number: string;
  expiry_date: string;
  cardholder_name: string;
}

export type AddCreditCardToDBData = Omit<AddCreditCardData, 'cardholder_name' | 'expiry_date' | 'card_number'> & {
  expiry_month: string;
  expiry_year: string;
};

export interface GetCreditCardDetailsRequest {
  jwt_cc_token: string;
}

export interface GetCreditCardDetailsData {
  card_number: string;
  card_expiry: string;
  card_cvv: string;
  card_holder: string;
  nickname: string;
  note: string;
  country: string;
  state?: string;
  address_line1: string;
  address_line2: string;
  city: string;
  zip: string;
  raw_phone_number: string | null;
}

export interface AddCreditCardRequest {
  card_holder: string;
  card_number: string;
  card_type: string;
  cvv: string;
  expiry: string;
  expiry_month: string;
  expiry_year: string;
  expiry_year_short: string;
  last_four: string;
}

export interface GetCardData {
  id: string;
  last4: string;
  card_type: string;
  nickname?: string | null;
  note?: string | null;
  is_expired: boolean;
  created_at: string | null;
  phone_number: string | null;
  raw_phone_number: string | null;
}

export interface VerifyCardDetailsRequest {
  cc_id: string;
  type: string;
}

export interface VerifyCardDetailsData {
  two_factor_code_id: string;
}

export interface VerifyCodeErrorFields {
  attempts: unknown;
  two_factor_code_id: unknown;
}

export interface VerifyCodeRequest {
  two_factor_code_id: string;
  code: string;
}

export interface VerifyCodeData {
  token: string;
}

export interface DeleteCardRequest {
  jwt_cc_token: string;
}

// Preferred (pinned) credit cards
export interface PreferredCreditCardItem {
  card_type: string;
  nickname?: string | null;
  last_four: string;
  created_at: string | null;
}

export interface PreferredCreditCard {
  id: string | null;
  credit_card_id: string;
  credit_card: PreferredCreditCardItem;
}

export interface SetPreferredCreditCardRequest {
  credit_card_id: string;
}

export type GetPreferredCreditCardsResponse = ResponseType<PreferredCreditCard[]>;
export type SetPreferredCreditCardResponse = ResponseType<PreferredCreditCard>;

export interface UpdateCardDetailsRequest {
  note?: string;
  nickname?: string;
  raw_phone_number?: string;
  country: string;
  state?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  zip: string;
}

export interface PrimaryContactInfoData {
  primary_email: Nullable<string>;
  phone_number: Nullable<string>;
}

export type GetPrimaryContactInfoResponse = ResponseType<PrimaryContactInfoData>;

export interface ShareSignatureData {
  signature: string;
}

export type GetShareSignatureResponse = ResponseType<ShareSignatureData>;

export interface ShareClientRequest {
  share_profile: boolean;
  share_credit_card: boolean;
  email: string;
  cc?: string;
  bcc?: string;
  message?: string;
  signature?: string;
  attachment_media_uuids?: string[];
}

export interface ShareClientResponse {
  message: string;
  data: {
    share_log_id: number;
  };
}

export type ClientStatus = 'PENDING' | 'ACTIVE' | 'ARCHIVED';

export interface ChangeClientStatusRequest {
  status: ClientCrmStatus;
}

export interface ClientSettings {
  id: number;
  client_id: number;
  profile_link: string;
  basic_information: boolean;
  emergency_contact: boolean;
  address: boolean;
  social_media: boolean;
  important_dates: boolean;
  important_numbers: boolean;
  related_contacts: boolean;
  diet: boolean;
  allergies: boolean;
  fly: boolean;
  stay: boolean;
  rental_car: boolean;
  amenities: boolean;
  wine: boolean;
  water: boolean;
  pillows: boolean;
  likes_and_dislikes: boolean;
  tasks: boolean;
  notes: boolean;
  created_by_id: number;
  updated_by_id: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateClientSettingsRequest {
  basic_information?: boolean;
  emergency_contact?: boolean;
  address?: boolean;
  social_media?: boolean;
  important_dates?: boolean;
  important_numbers?: boolean;
  related_contacts?: boolean;
  diet?: boolean;
  allergies?: boolean;
  fly?: boolean;
  stay?: boolean;
  rental_car?: boolean;
  amenities?: boolean;
  wine?: boolean;
  water?: boolean;
  pillows?: boolean;
  likes_and_dislikes?: boolean;
  tasks?: boolean;
  notes?: boolean;
}

export interface ClientSettings {
  id: number;
  client_id: number;
  profile_link: string;
  basic_information: boolean;
  emergency_contact: boolean;
  address: boolean;
  social_media: boolean;
  important_dates: boolean;
  important_numbers: boolean;
  related_contacts: boolean;
  diet: boolean;
  allergies: boolean;
  fly: boolean;
  stay: boolean;
  rental_car: boolean;
  amenities: boolean;
  wine: boolean;
  water: boolean;
  pillows: boolean;
  likes_and_dislikes: boolean;
  tasks: boolean;
  notes: boolean;
  created_by_id: number;
  updated_by_id: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateClientSettingsRequest {
  basic_information?: boolean;
  emergency_contact?: boolean;
  address?: boolean;
  social_media?: boolean;
  important_dates?: boolean;
  important_numbers?: boolean;
  related_contacts?: boolean;
  diet?: boolean;
  allergies?: boolean;
  fly?: boolean;
  stay?: boolean;
  rental_car?: boolean;
  amenities?: boolean;
  wine?: boolean;
  water?: boolean;
  pillows?: boolean;
  likes_and_dislikes?: boolean;
  tasks?: boolean;
  notes?: boolean;
}

export interface ClientAgentData {
  id: number;
  name: string;
}

export interface ClientData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  emails?: EmailData[];
  phone: string;
  phones?: PhoneData[];
  date_of_birth: string;
  identity: string;
  profile_picture_relative_url: string;
  profile_picture_url?: string;
  is_vip: boolean;
  status: ClientCrmStatus;
  full_name: string;
  agent: ClientAgentData | null;
}

export interface AgentData {
  id: number;
  name: string;
}

export interface AgentListData {
  id: number;
  full_name: string;
  type: string;
  image_relative_url: string | null;
}

export interface EditAgentRequest {
  agent_id: number;
}

export interface AddCreditCardToDBResponse {
  id: string;
  last4: string;
  card_type: string;
  phone_number: string | null;
  raw_phone_number: string | null;
}

export interface FrequentFlyerProgram {
  frequent_flyer: string;
  id: number;
  frequent_flyer_id: number;
  program_number: string;
}

export interface PassengerData {
  id: number;
  is_primary: boolean;
  client_identity?: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  dob: string;
  phone?: string;
  email?: string;
  is_international_flight: boolean;
  passport_files: AirRequestFileType[];
  flight_files: AirRequestFileType[];
  frequent_flyers?: FrequentFlyerProgram[];
}
export interface PreferredAirport {
  city: string;
  code: string;
  country: string;
  id: number;
}

export interface GetPreferredAirportData {
  city: string;
  iata_code: string;
  country: string;
  id: number;
}
export interface ClientDocument {
  document_name: string;
  document_type: string;
  id: number;
  url: string;
  created_at?: string;
  updated_at?: string;
}

export interface PhoneData {
  uuid?: Nullable<string>;
  title?: Nullable<string>;
  phone?: Nullable<string>;
  raw_phone?: Nullable<string>;
  is_primary?: boolean;
}

export interface EmailData {
  uuid?: Nullable<string>;
  title?: Nullable<string>;
  email?: Nullable<string>;
  is_primary?: boolean;
}

export interface SourceData {
  id: number;
  title: string;
}
export interface RelationshipData {
  id: number;
  name: string;
}
export interface ClientRelationshipData {
  id: number;
  description: string;
}

export type ClientDetailsData = {
  id: number;
  client_id: number;
  preferred_name: Nullable<string>;
  first_name: string;
  middle_name: Nullable<string>;
  last_name: string;
  full_name: string;
  is_vip: boolean;
  billing_same_as_mailing: Nullable<boolean>;
  gender: Nullable<string>;
  source: Nullable<SourceData>;
  email: Nullable<string>;
  additional_emails: ClientEmailData[];
  emails?: EmailData[];
  date_of_birth: Nullable<string>;
  phone: Nullable<string>;
  phones: PhoneData[];
  client_mailing_address: Nullable<string>;
  client_mailing_address_line_2: Nullable<string>;
  client_mailing_city: Nullable<string>;
  client_mailing_state: Nullable<string>;
  client_mailing_country: Nullable<string>;
  zip_code: Nullable<string>;
  client_billing_address: Nullable<string>;
  client_billing_address_line_2: Nullable<string>;
  client_billing_city: Nullable<string>;
  client_billing_state: Nullable<string>;
  client_billing_country: Nullable<string>;
  billing_zip_code: Nullable<string>;
  emergency_contact_name: Nullable<string>;
  emergency_relationship: Nullable<ClientRelationshipData>;
  emergency_contact_phone: Nullable<string>;
  emergency_contact_email: Nullable<string>;
  social_facebook_url: Nullable<string>;
  social_instagram_url: Nullable<string>;
  social_linkedin_url: Nullable<string>;
  social_twitter_url: Nullable<string>;
  social_custom_url: Nullable<string>;
  preferred_airports: Nullable<PreferredAirport[]>;
  profile_picture_url: Nullable<string>;
  documents: Nullable<ClientDocument[]>;
  userHasCertifiedTheInformation: boolean;
  is_draft: boolean;
  draft_identification: Nullable<string>;
  is_billing_same_as_mailing: boolean;
  relationship: ClientRelationshipData;
  tsa?: {
    id: number;
    client_id: number;
    global_entry: Nullable<string>;
    global_expiry_date: Nullable<string>;
    known_traveler_number: Nullable<string>;
    known_traveler_number_expire_date: Nullable<string>;
    clear_member?: Nullable<string>;
    membership_expiry_date?: Nullable<string>;
    created_at: string;
    updated_at: string;
  };
  loyalty_numbers?: LoyaltyNumbersData[];
  preferences?: {
    id: number;
    client_id: number;
    preference_data: GetClientPreferencesData;
    likes: Nullable<string>;
    dislikes: Nullable<string>;
    created_at: string;
    updated_at: string;
  };
  profile_picture_original_url: Nullable<string>;
  created_at: string;
  updated_at: string;
  created_by_id: number | null;
  updated_by_id: number | null;
};
export type ClientEmailData = { email: string | null };
export type ClientPhoneData = { title?: string | null; phone: string };
export interface DraftClientDetailsData {
  preferred_name?: string | null;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  is_vip: boolean;
  gender?: string;
  source_id?: number | null;
  email?: string | null;
  additional_emails?: ClientEmailData[];
  date_of_birth?: string | null;
  phone?: string | null;
  client_mailing_address?: string | null;
  billing_same_as_mailing?: boolean;
  client_mailing_address_line_2?: string | null;
  client_mailing_city?: string | null;
  client_mailing_state?: string | null;
  client_mailing_country?: string | null;
  zip_code?: string | null;
  client_billing_address?: string | null;
  client_billing_address_line_2?: string | null;
  client_billing_city?: string | null;
  client_billing_state?: string | null;
  client_billing_country?: string | null;
  billing_zip_code?: string | null;
  emergency_contact_name?: string | null;
  emergency_relationship_type_id?: number | null;
  emergency_contact_phone?: string | null;
  emergency_contact_email?: string | null;
  social_facebook_url?: string | null;
  social_instagram_url?: string | null;
  social_linkedin_url?: string | null;
  social_twitter_url?: string | null;
  social_custom_url?: string | null;
  profile_picture_storage_id?: string | null;
  profile_picture_original_storage_id?: string | null;
  preferred_airport_ids?: number[] | null;
  phones?: ClientPhoneData[];
}

export type GetPassportClientData = {
  id: number;
  client_id: number;
  document_type: string;
  document_name: Nullable<string>;
  document_url: Nullable<string>;
  passport_number: Nullable<string>;
  expiry_date: Nullable<string>;
  issue_date: Nullable<string>;
  issuance_country: Nullable<string>;
  first_name: Nullable<string>;
  middle_name: Nullable<string>;
  last_name: Nullable<string>;
  date_of_birth: Nullable<string>;
  is_under_18: boolean;
  created_at: Nullable<string>;
  updated_at: Nullable<string>;
};

export type PassportClientData = {
  passport_number: string;
  expiry_date: string;
  passport_id: number;
};

export type CreateClientPassportData = {
  passport_number: string;
  expiry_date: string;
};

export type CreateClientPassportResponse = ResponseType<{
  id: number;
  client_id: number;
  document_type: string;
  document_name: string | null;
  document_url: string | null;
  passport_number: string | null;
  expiry_date: string | null;
  issue_date: string | null;
  issuance_country: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  is_under_18: boolean;
  created_at: string | null;
  updated_at: string | null;
}>;
export interface UpdatePassportClientData {
  passport_number: Nullable<string>;
  expiry_date: Nullable<string>;
}

export interface CreateImportantDatePayload {
  name: string;
  event_date: string;
  event_frequency_id: Nullable<number>;
  client_id: number;
  notes?: string | null;
}

export interface UpdateImportantDatePayload {
  name?: string;
  event_date?: string;
  event_frequency_id?: Nullable<number>;
  notes?: string | null;
}

export interface EventFrequencyData {
  id: number;
  description: string;
}

export interface ImportantDateData {
  id: number;
  name: string;
  event_date: string;
  event_frequency_id: Nullable<number>;
  event_frequency: EventFrequencyData;
  notes?: string | null;
}

export interface ClientListData {
  full_name: string;
  id: number;
  profile_photo_url: Nullable<string>;
}

export interface ClientsDashboardQueryParams {
  is_draft?: number;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  agent_id?: number;
  status?: ClientCrmStatus;
  sort?: string;
  page?: number;
  per_page?: number;
}

export interface ClientsDashboardListAddress {
  client_mailing_address: string;
  client_mailing_address_line_2: string;
  client_mailing_city: string;
  client_mailing_country: string;
  client_mailing_state: string;
  zip_code: string;
}

export interface ClientsDashboardListAgent {
  id: number;
  name: string;
}

export interface ClientsDashboardListItem {
  id: number;
  full_name: string;
  email?: string;
  emails?: EmailData[];
  phone?: Nullable<string>;
  phones?: PhoneData[];
  status: ClientCrmStatus;
  agent_id: Nullable<number>;
  agent: Nullable<ClientsDashboardListAgent>;
  address: ClientsDashboardListAddress;
  profile_picture_url?: Nullable<string>;
}

export type GetClientsDashboardResponse = ResponseDataWithPagination<ClientsDashboardListItem[]>;

export type ImportClientSource = 'smartFlyer' | 'TravelJoy' | 'ClientBase';

export const IMPORT_SOURCE_OPTIONS: SelectorOption<ImportClientSource>[] = [
  { label: 'Generic template', value: 'smartFlyer' },
  { label: 'Travel Joy', value: 'TravelJoy' },
  { label: 'Client base', value: 'ClientBase' },
];

export type ImportClientType = 'primary' | 'related' | 'loyalty-programs';

export const IMPORT_TYPE_OPTIONS: SelectorOption<ImportClientType>[] = [
  { label: 'Primary', value: 'primary' },
  { label: 'Related', value: 'related' },
  { label: 'Loyalty Programs', value: 'loyalty-programs' },
];

export interface ImportPrimaryClientsMetadata {
  agentId?: number;
  importSource?: string;
  importType?: ImportClientType;
}

export type ImportRelatedClientsMetadata = Omit<ImportPrimaryClientsMetadata, 'agentId'>;
export type ImportLoyaltyProgramsMetadata = Omit<ImportPrimaryClientsMetadata, 'agentId'>;

export interface BulkClientsGroupedStatusUpdateRequest {
  clients_id: number[];
  status: ClientCrmStatus;
}

export interface BulkClientsGroupedStatusUpdateResponseData {
  updated_count: number;
  client_ids: number[];
  status: ClientCrmStatus;
}

export type BulkClientsGroupedStatusUpdateResponse = ResponseType<BulkClientsGroupedStatusUpdateResponseData>;

export interface BulkClientsGroupedDeleteRequest {
  clients_id: number[];
}

export interface BulkClientsGroupedDeleteResponseData {
  deleted_count: number;
  client_ids: number[];
}

export type BulkClientsGroupedDeleteResponse = ResponseType<BulkClientsGroupedDeleteResponseData>;

export interface ImportPrimaryClientsRequest {
  media_uuid: string;
  metadata?: ImportPrimaryClientsMetadata;
}

export interface ImportRelatedClientsRequest {
  media_uuid: string;
  metadata?: ImportRelatedClientsMetadata;
}

export interface ImportLoyaltyProgramsRequest {
  media_uuid: string;
  metadata?: ImportLoyaltyProgramsMetadata;
}

export interface ImportedValidClient {
  first_name: string;
  last_name: string;
  email: string;
  [key: string]: unknown;
}

export interface ImportedInvalidClient {
  row: number;
  errors: Record<string, string[]>;
  data: {
    first_name?: string;
    last_name?: string;
    email?: string;
    [key: string]: unknown;
  };
}

export interface ImportPrimaryClientsData {
  valid_count: number;
  invalid_count: number;
  valid: ImportedValidClient[];
  invalid: ImportedInvalidClient[];
}

export type ImportPrimaryClientsResponse = ResponseType<ImportPrimaryClientsData>;
export type ImportRelatedClientsResponse = ImportPrimaryClientsResponse;
export type ImportLoyaltyProgramsResponse = ImportPrimaryClientsResponse;

export type GetPassportClientDataResponse = ResponseType<GetPassportClientData[]>;
export type GetImportantDatesResponse = ResponseType<ImportantDateData[]>;
export type EmailWriteAction = {
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  uuid?: string;
  title?: string;
  email?: string;
  is_primary?: boolean;
};

export type PhoneWriteAction = {
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  uuid?: string;
  title?: string;
  raw_phone?: string;
  is_primary?: boolean;
};

export type UpdateClientDetailsRequest = Omit<
  DraftClientDetailsData,
  'email' | 'additional_emails' | 'phone' | 'phones'
> & {
  emails?: EmailWriteAction[];
  phones?: PhoneWriteAction[];
};
export type AddCreditCardToVaultResponse = AddCreditCardToVaultData;
export type AddCreditCardPresignedUrlResponse = ResponseType<AddCreditCardPresignedUrlData>;
export type AddCreditCardResponse = ResponseType<AddCreditCardToDBResponse>;
export type GetCreditCardDetailsResponse = ResponseType<GetCreditCardDetailsData>;
export type GetCardsListResponse = ResponseType<GetCardData[]>;
export type GetBillingAddressResponse = ResponseType<BillingAddressData>;
export type VerifyCardDetailsResponse = ResponseType<VerifyCardDetailsData>;
export type GetClientResponse = ResponseType<ClientData>;
export type VerifyCodeResponse = ResponseType<VerifyCodeData>;
export type GetClientSettingsResponse = ResponseType<ClientSettings>;
export type UpdateClientSettingsResponse = ResponseType<ClientSettings>;
export type GetAgentsResponse = ResponseType<AgentData[]>;
export type GetAgentsListResponse = ResponseDataWithPagination<AgentListData[]>;
export type GetClientDetailsResponse = ResponseType<GetClientData>;
export type GetClientDetailsDraftResponse = ResponseType<ClientDetailsData>;
export type GetClientListResponse = ResponseDataWithPagination<ClientListData[]>;
